import { useMyMonitoringGetSiteUsers } from '@apps-common/apis/my-monitoring/users'
import { MyMonitoringUserModifyModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/users/modals/my-monitoring-modify-modal'
import { SystemUserModifyModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/users/modals/system-user-modify-modal'
import { PullRefresh } from '@apps-common/components/common'
import { MyMonitoringLogoOnly } from '@apps-common/components/common/logos'
import { PageSubtitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundLg, ButtonRoundSm, ButtonTab } from '@apps-common/components/elements'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { useMyMonitoringSiteContextStore } from '@apps-common/stores'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import classNames from 'classnames'
import { useState } from 'react'
import { UnitUserType } from '../../site-manage/modals'
import { SiteMyMonitoringUsers } from './SiteMyMonitoringUsers'
import { SiteSystemUsers } from './SiteSystemUsers'

interface IProps {
  loading: boolean
  site?: IMMSiteModel
  back: () => void
}

export const SiteUserView: React.FC<IProps> = ({ loading, site, back }) => {
  const [tab, setTab] = useState<UnitUserType>('myMonitoring')
  const { isAdmin } = useMyMonitoringSiteContextStore()

  const { data, isLoading, isError, refetch } = useMyMonitoringGetSiteUsers({
    config: { enabled: !!site?.unitId },
    unitId: site?.unitId || ''
  })

  loading = loading || !site || !data || isLoading || isError

  return (
    <>
      <PullRefresh loading={loading} callback={refetch} />
      <div className=' flex flex-col gap-4 max-w-[430px] lg:max-w-full mx-auto'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center justify-start gap-4'>
            <ButtonRoundSm
              type='button'
              className='bg-ctx-mm-brand text-ctx-on-mm-brand'
              callback={() => {
                if (back) {
                  back()
                }
              }}
            >
              <commonIcons.arrowRight className='rotate-180' />
            </ButtonRoundSm>
            <PageSubtitle classes='text-text'>Users</PageSubtitle>
          </div>
          {isAdmin && (
            <>
              {tab === 'myMonitoring' && (
                <ModalButton
                  component={MyMonitoringUserModifyModal}
                  disabled={loading}
                  componentProps={{
                    unit: {
                      unitId: site?.unitId,
                      ipAddress: site?.ipAddress
                    },
                    existingIds: data?.myMonitoringUsers?.map((u) => u.id) || []
                  }}
                  enterAnimation={modalSlideUpEnterAnimation}
                  leaveAnimation={modalSlideUpLeaveAnimation}
                >
                  <ButtonRoundLg
                    type='div'
                    loading={loading}
                    disabled={loading}
                    className='action-success'
                    tooltip={{ id: 'device-add-new-my-monitoring-user', place: 'bottom', message: 'Add new myMonitoring user' }}
                  >
                    <commonIcons.add className='!text-[1.1rem]' />
                  </ButtonRoundLg>
                </ModalButton>
              )}
              {tab === 'system' && (
                <ModalButton
                  component={SystemUserModifyModal}
                  disabled={loading}
                  componentProps={{
                    unit: {
                      unitId: site?.unitId,
                      ipAddress: site?.ipAddress
                    }
                  }}
                  enterAnimation={modalSlideUpEnterAnimation}
                  leaveAnimation={modalSlideUpLeaveAnimation}
                >
                  <ButtonRoundLg
                    type='div'
                    loading={loading}
                    disabled={loading}
                    className='action-success'
                    tooltip={{ id: 'device-add-new-system-user', place: 'bottom', message: 'Add new system user' }}
                  >
                    <commonIcons.add className='!text-[1.1rem]' />
                  </ButtonRoundLg>
                </ModalButton>
              )}
            </>
          )}
        </div>
        <div className='flex gap-[10px] sm:gap-[16px] mb-3'>
          <ButtonTab
            classes={classNames('w-full max-h-[28px] lg:w-auto lg:min-w-[180px]', tab === 'myMonitoring' && 'ring-2 ring-ctx-mm-brand')}
            active={false}
            callback={() => setTab('myMonitoring')}
          >
            <MyMonitoringLogoOnly color='fill-text/70' classes='w-[19px]' />
            myMonitoring
          </ButtonTab>
          <ButtonTab
            classes={classNames('w-full max-h-[28px] lg:w-auto lg:min-w-[180px]', tab === 'system' && 'ring-2 ring-ctx-mm-brand')}
            active={false}
            callback={() => setTab('system')}
          >
            <commonIcons.users />
            System
          </ButtonTab>
        </div>
        {tab === 'myMonitoring' && (
          <SiteMyMonitoringUsers loading={loading} site={site} data={data?.myMonitoringUsers} disabled={!isAdmin} />
        )}
        {tab === 'system' && <SiteSystemUsers loading={loading} site={site} data={data?.namedUsers} disabled={!isAdmin} />}
      </div>
    </>
  )
}
